<template>
	<div class="wrapper">
		<!-- Totals per scraper over the selected period -->
		<div class="vx-row mb-4">
			<div class="vx-col w-full md:w-1/4 mb-4">
				<vx-card>
					<div class="text-center">
						<h5 class="mb-2">Exécutions</h5>
						<h3 class="text-primary">{{ totals.runs }}</h3>
						<small>{{ periodLabel }}</small>
					</div>
				</vx-card>
			</div>
			<div class="vx-col w-full md:w-1/4 mb-4">
				<vx-card>
					<div class="text-center">
						<h5 class="mb-2">Réussies</h5>
						<h3 class="text-success">{{ totals.success }}</h3>
						<small>{{ totals.items_ok }} éléments</small>
					</div>
				</vx-card>
			</div>
			<div class="vx-col w-full md:w-1/4 mb-4">
				<vx-card>
					<div class="text-center">
						<h5 class="mb-2">Échouées</h5>
						<h3 class="text-danger">{{ totals.failure }}</h3>
						<small>{{ totals.items_failed }} éléments</small>
					</div>
				</vx-card>
			</div>
			<div class="vx-col w-full md:w-1/4 mb-4">
				<vx-card>
					<div class="text-center">
						<h5 class="mb-2">Dernière exécution</h5>
						<h3 class="text-primary">{{ lastRunLabel }}</h3>
						<small v-if="totals.running > 0" class="text-warning"
							>{{ totals.running }} en cours</small
						>
					</div>
				</vx-card>
			</div>
		</div>

		<!-- Filters -->
		<vx-card class="mb-4">
			<div class="vx-row">
				<div class="vx-col w-full md:w-1/4 mb-2">
					<label class="vs-select--label">Scraper</label>
					<vs-select v-model="filters.scraper" class="w-full" autocomplete>
						<vs-select-item value="" text="Tous" />
						<vs-select-item
							v-for="name in scrapers"
							:key="name"
							:value="name"
							:text="name"
						/>
					</vs-select>
				</div>
				<div class="vx-col w-full md:w-1/4 mb-2">
					<label class="vs-select--label">Statut</label>
					<vs-select v-model="filters.status" class="w-full">
						<vs-select-item
							v-for="item in statusOptions"
							:key="item.value"
							:value="item.value"
							:text="item.text"
						/>
					</vs-select>
				</div>
				<div class="vx-col w-full md:w-1/4 mb-2">
					<label class="vs-select--label">Période</label>
					<vs-select v-model="filters.days" class="w-full">
						<vs-select-item
							v-for="item in periodOptions"
							:key="item.value"
							:value="item.value"
							:text="item.text"
						/>
					</vs-select>
				</div>
				<div class="vx-col w-full md:w-1/4 mb-2 flex items-end">
					<vs-button
						icon="refresh"
						type="border"
						class="w-full"
						:disabled="isLoading"
						@click="fetchAll"
						>Actualiser</vs-button
					>
				</div>
			</div>
		</vx-card>

		<!-- Runs -->
		<vs-table
			max-items="50"
			pagination
			:data="runs"
			noDataText="Aucune exécution pour ces filtres."
		>
			<template slot="thead">
				<vs-th style="pointer-events: auto" sort-key="started_at">Début</vs-th>
				<vs-th style="pointer-events: auto" sort-key="scraper">Scraper</vs-th>
				<vs-th style="pointer-events: auto" sort-key="target">Cible</vs-th>
				<vs-th style="pointer-events: auto" sort-key="server">Serveur</vs-th>
				<vs-th style="pointer-events: auto" sort-key="status">Statut</vs-th>
				<vs-th style="pointer-events: auto" sort-key="success_count">Réussis</vs-th>
				<vs-th style="pointer-events: auto" sort-key="failed_count">Échoués</vs-th>
				<vs-th style="pointer-events: auto" sort-key="duration_ms">Durée</vs-th>
			</template>
			<template slot-scope="{ data }">
				<vs-tr
					v-for="(run, indextr) in data"
					:key="run.id"
					:data="run"
				>
					<vs-td class="text-left">{{ formatDateTime(run.started_at) }}</vs-td>
					<vs-td class="text-left">{{ run.scraper }}</vs-td>
					<vs-td class="text-left target-cell" :title="run.target">{{
						run.target || "—"
					}}</vs-td>
					<vs-td class="text-left">{{ run.server || "—" }}</vs-td>
					<vs-td class="text-left">
						<vs-chip :color="statusColor(run.status)">{{
							statusLabel(run.status)
						}}</vs-chip>
					</vs-td>
					<vs-td class="text-left">{{ run.success_count }}</vs-td>
					<vs-td class="text-left">
						<span :class="{ 'text-danger font-bold': run.failed_count > 0 }">{{
							run.failed_count
						}}</span>
					</vs-td>
					<vs-td class="text-left">{{ formatDuration(run.duration_ms) }}</vs-td>

					<template slot="expand">
						<div class="expand-body">
							<div class="vx-row">
								<div class="vx-col w-full md:w-1/2 mb-2">
									<p><strong>Fin :</strong> {{ formatDateTime(run.finished_at) }}</p>
									<p><strong>Cible :</strong> {{ run.target || "—" }}</p>
								</div>
								<div class="vx-col w-full md:w-1/2 mb-2">
									<p v-if="run.error_message">
										<strong>Erreur :</strong>
										<span class="text-danger">{{ run.error_message }}</span>
									</p>
									<p v-else class="text-success">Aucune erreur signalée.</p>
								</div>
							</div>
							<div v-if="run.details">
								<strong>Détails :</strong>
								<pre class="details-json">{{ prettyDetails(run.details) }}</pre>
							</div>
						</div>
					</template>
				</vs-tr>
			</template>
		</vs-table>
	</div>
</template>

<script>
import moment from "moment";
import ScrapingRun from "@/entities/ScrapingRun";

export default {
	name: "ScrapingLogs",
	data() {
		return {
			isLoading: false,
			runs: [],
			scrapers: [],
			summary: [],
			filters: {
				scraper: "",
				status: "",
				days: 7,
			},
			statusOptions: [
				{ value: "", text: "Tous" },
				{ value: "success", text: "Réussi" },
				{ value: "failure", text: "Échoué" },
				{ value: "running", text: "En cours" },
			],
			periodOptions: [
				{ value: 1, text: "24 heures" },
				{ value: 7, text: "7 jours" },
				{ value: 30, text: "30 jours" },
				{ value: 90, text: "90 jours" },
			],
		};
	},
	computed: {
		/** Sum of the per-scraper summary, honouring the scraper filter. */
		totals() {
			const rows = this.filters.scraper ?
				this.summary.filter((s) => s.scraper === this.filters.scraper) :
				this.summary;
			const sum = (key) => rows.reduce((acc, r) => acc + Number(r[key] || 0), 0);
			const lastRun = rows.reduce(
				(acc, r) => (r.last_run && (!acc || r.last_run > acc) ? r.last_run : acc),
				null
			);
			return {
				runs: sum("runs"),
				success: sum("success"),
				failure: sum("failure"),
				running: sum("running"),
				items_ok: sum("items_ok"),
				items_failed: sum("items_failed"),
				last_run: lastRun,
			};
		},
		periodLabel() {
			const opt = this.periodOptions.find((o) => o.value === this.filters.days);
			return opt ? `sur ${opt.text}` : "";
		},
		lastRunLabel() {
			return this.totals.last_run ? moment(this.totals.last_run).fromNow() : "—";
		},
	},
	watch: {
		"filters.scraper": "fetchRuns",
		"filters.status": "fetchRuns",
		"filters.days": "fetchAll",
	},
	created() {
		moment.locale("fr");
		this.fetchAll();
	},
	methods: {
		async fetchAll() {
			this.isLoading = true;
			try {
				const [scrapers, summary] = await Promise.all([
					ScrapingRun.scrapers(),
					ScrapingRun.summary(this.filters.days),
				]);
				this.scrapers = scrapers;
				this.summary = summary.scrapers;
				await this.fetchRuns();
			} catch (err) {
				this.notifyError(err);
			} finally {
				this.isLoading = false;
			}
		},
		async fetchRuns() {
			try {
				this.runs = await ScrapingRun.list({
					scraper: this.filters.scraper,
					status: this.filters.status,
					days: this.filters.days,
					limit: 500,
				});
			} catch (err) {
				this.notifyError(err);
			}
		},
		notifyError(err) {
			console.log(err);
			this.$vs.notify({
				title: "Scraping",
				text: "Impossible de charger les exécutions.",
				color: "danger",
			});
		},
		formatDateTime(date) {
			return date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : "—";
		},
		formatDuration(ms) {
			if (ms === null || ms === undefined) return "—";
			if (ms < 1000) return `${ms} ms`;
			const s = Math.round(ms / 1000);
			if (s < 60) return `${s} s`;
			const m = Math.floor(s / 60);
			return `${m} min ${s % 60} s`;
		},
		statusColor(status) {
			return { success: "success", failure: "danger", running: "warning" }[status] || "dark";
		},
		statusLabel(status) {
			return (
				{ success: "Réussi", failure: "Échoué", running: "En cours" }[status] || status
			);
		},
		prettyDetails(details) {
			// mysql2 returns JSON columns already parsed; older drivers return a string.
			const value = typeof details === "string" ? JSON.parse(details) : details;
			return JSON.stringify(value, null, 2);
		},
	},
};
</script>

<style scoped>
.wrapper {
	text-align: left;
}
.target-cell {
	max-width: 260px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.expand-body {
	padding: 0.75rem 1rem;
	width: 100%;
}
.details-json {
	max-height: 320px;
	overflow: auto;
	padding: 0.5rem;
	font-size: 0.8rem;
	background: rgba(0, 0, 0, 0.04);
	border-radius: 4px;
}
</style>
